import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'react-router';
import {useDebounceFn} from 'ahooks';
import toast from 'react-hot-toast';
import { isMockHomeworkPreview } from '@/utils/homeworkSession';
import global from '@/utils/global';
import { isSufeExamClient } from '@/utils/examClient';
import { isRecordReview } from '@/utils/recordReview';
import { IClearHighlightPayload, IDeleteNotePayload, IUpdatePanel, IhightLightItem } from '@/utils/ctype';
import { logError } from '@/utils/logger/arms-config';
import store, { useAppSelector} from '@/stores/index';
import { useAppDispatch } from '@/stores/index';
import {getWritingAsync, getAnswerListAsync, setPanelDataByIndex, setFocusIndex,
  saveAnswer, saveAnswerListAsync, updateTimeState, updateRecordId, 
  saveHighlightData, submitAnswerAsync, toggleShowNotePanel, clearHightLightInfo, updatePanelInfo, deleteNote, resetExamLoadState} from '@/stores/slice/writing';
import { getStudentInfoAsync } from '@/stores/slice/user';
import { saveAnswerList } from '@/api/writing';
import NotePanel from '../../components/NotePanel';
import Bottom from '../../components/Bottom';
import Main from '../../components/Main';
import { useExamSubmission } from '../../useExamSubmission';
import Loading from '../../components/Loading';
import ExamLoadingNotice from '../../components/ExamLoadingNotice';
import { useExamLoading } from '../../useExamLoading';
import { Score } from '../../components/Score';
import MainContent from './MainContent';
import style from './index.module.less';



export default function MockWriting() {

  const preview = isMockHomeworkPreview();
  global.subject = 'write';
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const params = useParams();
  const dispatch = useAppDispatch();
  const isAI = location.pathname.includes('/mockwritingai');
  const readOnly = isRecordReview(location.search);
  const {showNotePanel, answerAll, limits, currentFocus, pageDataIndex, 
    loadAnswered, highLightInfo, pageDataCache, surplus_time, draftSyncedAt, essay, big_essay, recordId} = useAppSelector(state => state.writing);
  const routeRecordId = Number(params.recordId);

  useEffect(() => {
    dispatch(updateRecordId(routeRecordId));
    dispatch(resetExamLoadState());
  }, [dispatch, routeRecordId]);

  const loadExam = useCallback(async (signal: AbortSignal) => {
    const answerRequest = dispatch(getAnswerListAsync({record_id: routeRecordId, signal}));
    const contentRequest = dispatch(getWritingAsync({record_id: routeRecordId, signal}));
    try {
      await Promise.all([answerRequest.unwrap(), contentRequest.unwrap()]);
    } catch (error) {
      answerRequest.abort();
      contentRequest.abort();
      await Promise.allSettled([answerRequest, contentRequest]);
      throw error;
    }
  }, [dispatch, routeRecordId]);
  const {ready: draftLoaded, status: loadingStatus, retry: retryLoad} = useExamLoading({recordId: routeRecordId, load: loadExam});
  const {showLoading, submitHandle, isFinalizing} = useExamSubmission({
    recordId: routeRecordId,
    course: 'writing',
    ready: draftLoaded && !isAI && !readOnly && !preview,
    expired: !isAI && !readOnly && !preview && surplus_time <= 0,
    save: signal => dispatch(saveAnswerListAsync({recordId: routeRecordId, signal})).unwrap(),
    submit: signal => dispatch(submitAnswerAsync({recordId: routeRecordId, signal})).unwrap(),
  });

  useEffect(() => {
    if (!isAI && !readOnly && !preview && draftLoaded) {
      const timer = setInterval(() => {
        if (!isFinalizing()) dispatch(saveAnswerListAsync());
      }, 10000);
      return () => {
        clearInterval(timer);
      };
    }
  }, [draftLoaded, isAI, readOnly, preview, dispatch]);

  // 更新时间
  useEffect(() => {
    if (!isAI && !readOnly && !preview && draftLoaded) {
      const lastTime = [0, 0];
      const fn = () => {
        if (isFinalizing()) return;
        const writing = store.getState().writing;
        const idx = writing.pageDataIndex;
        try {
          if (idx !== 0 && idx !== 1) {
            return;
          }

          if (lastTime[0] + lastTime[1] > 5) {
            const obj: any = {
              record_id: routeRecordId,
            };
            if (lastTime[0] > 0) {
              obj['task1_duration'] = lastTime[0];
              lastTime[0] = 0;
            }
            if (lastTime[1] > 0) {
              obj['task2_duration'] = lastTime[1];
              lastTime[1] = 0;
            }
            saveAnswerList(obj);
          } else {
            lastTime[idx] = 1 + lastTime[idx];
          }

        } catch (e) {
          logError(e as any);
        }
      };
      const timer = setInterval(fn, 1000);
      return () => {
        clearInterval(timer);
        fn();
      };
    }
  }, [draftLoaded, isAI, readOnly, preview, routeRecordId]);

  // Payment redirects retain manual submission for regular browsers.
  useEffect(() => {
    if (query.get('submit') === '1' && draftLoaded && !isAI && !readOnly && !preview && !isSufeExamClient()) {
      void submitHandle(() => undefined);
    }
  }, [draftLoaded, isAI, readOnly, preview, submitHandle]);

  useEffect(() => {
    dispatch(getStudentInfoAsync());
  }, [dispatch, routeRecordId]);
  
  const setPanelHandle = (index: number) => {
    dispatch(setPanelDataByIndex({index}));
  };

  const setFocusIndexHandle = (index: number) => {
    dispatch(setFocusIndex({index}));
  };

  const { run, cancel, flush } = useDebounceFn(() => {
    if (!readOnly && !preview && draftLoaded && !isFinalizing()) {
      dispatch(saveAnswerListAsync());
    }
  }, { wait: 1500, });
  useEffect(() => cancel, [cancel, routeRecordId]);

  const saveAnswerHandle = (obj: {key: string; value: string;}) => {
    if (readOnly || preview || !draftLoaded || isFinalizing()) {
      return;
    }
    toast.dismiss('writing-length');
    dispatch(saveAnswer(obj));
    run();
  };

  const saveHighlightDataHandle =(params: {show: boolean;wrapUid: string;} & IhightLightItem) => {
    if (readOnly || preview || !draftLoaded || isFinalizing()) {
      return;
    }
    dispatch(saveHighlightData(params));
    run();
  };

  const updateTimeHandle = (value: number) => {
    if (!readOnly && !preview) dispatch(updateTimeState(value));
  };

  const noteHandle = () => {
    dispatch(toggleShowNotePanel());
  };

  const clearDialogHandle= (payload?: IClearHighlightPayload) => {
    if (readOnly || preview || !draftLoaded || isFinalizing()) {
      return;
    }
    dispatch(clearHightLightInfo(payload));
    run();
  };

  const updateDialogInfo = (info: IUpdatePanel) => {
    if (readOnly || preview || !draftLoaded || isFinalizing()) {
      return;
    }
    dispatch(updatePanelInfo(info));
    run();
    flush();
  };

  const deleteNoteHandle = (payload: IDeleteNotePayload) => {
    if (readOnly || preview || !draftLoaded || isFinalizing()) {
      return;
    }
    dispatch(deleteNote(payload));
    run();
  };

  const scoreNode = useMemo(() => {
    if (!isAI || !pageDataCache || !pageDataCache[pageDataIndex]) {
      return null;
    }
    return <Score type={pageDataIndex === 0 ? 5 : 4} value={pageDataCache[pageDataIndex].question.score} recordId={recordId}/>;
  }, [isAI, recordId, pageDataIndex, pageDataCache]);

  return (
    <div className={style.viewBox}>
      <Main course="w" pageData={pageDataCache[pageDataIndex]} time={surplus_time}
        draftSyncedAt={draftSyncedAt}
        loadAnswered={draftLoaded && loadAnswered} highLightObj={highLightInfo[pageDataIndex]}
        updateTimeHandle={updateTimeHandle} saveHighlightDataHandle={saveHighlightDataHandle}
        submitHandle={submitHandle} noteHandle={noteHandle} showNotePanel={showNotePanel}
        headerChildren={scoreNode}
        isAI={isAI || readOnly}
        readOnly={readOnly}
        clearDialogHandle={clearDialogHandle}
        notePanel={<NotePanel readOnly={readOnly} showNotePanel={showNotePanel} highLightInfo={highLightInfo} noteHandle={noteHandle}
          updateNoteHandle={updateDialogInfo} deleteNoteHandle={deleteNoteHandle}></NotePanel>}>
        {
          pageDataCache.map((pageDataItem, index: number) => {
            return (
              pageDataItem ? <MainContent isAI={isAI || readOnly} key={index} value={index === 0 ? essay : big_essay} saveAnswerHandle={saveAnswerHandle} pageData={pageDataItem} index={index} boxStyle={{display: pageDataIndex === index ? '' : 'none'}}></MainContent> : null
            );
          })
        }
      </Main>
      <Bottom limits={limits} answerAll={answerAll} currentFocus={currentFocus} setPanelHandle={setPanelHandle} pageDataIndex={pageDataIndex} setFocusIndexHandle={setFocusIndexHandle} showNotePanel={showNotePanel}></Bottom>
      <ExamLoadingNotice status={loadingStatus} onRetry={retryLoad} />
      <Loading show={showLoading}></Loading>
    </div>
  );
}
